import { UserOutlined } from '@ant-design/icons';
import { Row, Col } from 'antd';
import config from 'config';
import { Routes } from 'config/routes';
import { PermissionsEnum, RoleOutput, UserRole } from 'gql/types';
import { PermissionsType } from 'modules/user/types';
import { getTranslation } from 'utils/getTranslation';

import { MenuOptionType } from '../types';

const t = getTranslation('admin.Navigation');

export const getMenuItemsOfInternalTeamPreset = (
  userRole: UserRole
): MenuOptionType[] => [
  {
    key: Routes.Profile,
    label: (
      <Row>
        <Col>
          <UserOutlined />
        </Col>
        <Col offset={1}>{t(userRole.toLowerCase())}</Col>
      </Row>
    ),
  },
  {
    key: Routes.Users,
    label: t('users'),
    permissions: [PermissionsEnum.ViewUsersList],
  },
  {
    key: Routes.Requests,
    label: t('requestsForApproval'),
    permissions: [PermissionsEnum.ViewRequestsForApproval],
  },
  ...(config.FEATURES.APPOINTMENTS_PAGE_ENABLED
    ? [
        {
          key: Routes.Appointments,
          label: t('appointments'),
          permissions: [PermissionsEnum.ViewAppointments],
        },
      ]
    : []),
  ...(config.FEATURES.TASK_SCHEDULE_PAGE_ENABLED
    ? [
        {
          key: Routes.Schedule,
          label: t('schedule'),
          permissions: [
            PermissionsEnum.ViewTasksSchedule,
            PermissionsEnum.ViewAppointments,
          ],
        },
      ]
    : []),
  {
    key: Routes.Locations,
    label: t('locations'),
    permissions: [],
  },
  {
    key: Routes.Donors,
    label: t('donors'),
    children: [
      {
        key: Routes.IndividualDonors,
        label: t('individualDonorList'),
        permissions: [PermissionsEnum.ViewTheListOfDonors],
      },
      {
        key: Routes.GroupDonors,
        label: t('groupDonorList'),
        permissions: [PermissionsEnum.ViewTheListOfDonors],
      },
      {
        key: Routes.ReserveDonors,
        label: t('trainingReserveDonors'),
        permissions: [PermissionsEnum.ViewTheListOfTrainingReserveDonors],
      },
      {
        key: Routes.AppointmentsDonors,
        label: t('appointmentsDonors'),
        permissions: [PermissionsEnum.ViewTheListOfDonors],
      },
    ],
    permissions: [PermissionsEnum.ViewTheListOfDonors],
  },
  {
    key: Routes.Labwork,
    label: t('labwork'),
    permissions: [PermissionsEnum.ViewLabworkResults],
    roles: [
      UserRole.Admin,
      UserRole.MedicalDirector,
      UserRole.Dvm,
      UserRole.Operations,
      UserRole.RegionalUser,
      UserRole.Phlebotomist,
      UserRole.CanineHandler,
      UserRole.TeamLead,
    ],
  },
  {
    key: Routes.DvmExam,
    label: t('dvmExam'),
    // TODO: add permissions, similar to permissions: [PermissionsEnum.ViewDvmExams],
    roles: [
      UserRole.Admin,
      UserRole.MedicalDirector,
      UserRole.Dvm,
      UserRole.Operations,
      UserRole.RegionalUser,
      UserRole.Phlebotomist,
      UserRole.CanineHandler,
      UserRole.TeamLead,
    ],
  },
  {
    key: Routes.Owners,
    label: t('owners'),
    permissions: [PermissionsEnum.ViewTheListOfOwners],
  },
  {
    key: `${Routes.CollectionDonations}`,
    label: t('donationDataCollectionPage'),
    permissions: [PermissionsEnum.ViewDonationDataCollectionPage],
  },
  {
    key: Routes.Products,
    label: t('productList'),
    permissions: [PermissionsEnum.ViewProductList],
  },
  {
    key: Routes.NightlyCount,
    label: t('nightlyCount'),
    roles: [
      UserRole.Admin,
      UserRole.MedicalDirector,
      UserRole.Dvm,
      UserRole.Operations,
      UserRole.RegionalUser,
      UserRole.TeamLead,
      UserRole.Phlebotomist,
      UserRole.CanineHandler,
    ],
  },
  {
    key: Routes.Orders,
    label: t('orders'),
    permissions: [PermissionsEnum.ViewListOfOrders],
  },
  {
    key: Routes.Dashboards,
    label: t('dashboards'),
    children: [
      {
        key: Routes.DashboardsOperations,
        label: t('operations'),
        permissions: [PermissionsEnum.ViewReports],
        roles: [
          UserRole.Admin,
          UserRole.MedicalDirector,
          UserRole.Dvm,
          UserRole.Operations,
          UserRole.RegionalUser,
          UserRole.TeamLead,
          UserRole.Phlebotomist,
        ],
      },
      {
        key: Routes.DashboardsPhlebotomist,
        label: t('phlebotomist'),
        permissions: [PermissionsEnum.ViewReports],
        roles: [
          UserRole.Admin,
          UserRole.MedicalDirector,
          UserRole.Dvm,
          UserRole.TeamLead,
          UserRole.Phlebotomist,
          UserRole.Operations,
        ],
      },
      {
        key: Routes.DashboardsRecruiter,
        label: t('recruiter'),
        permissions: [PermissionsEnum.ViewReports],
        roles: [
          UserRole.Admin,
          UserRole.MedicalDirector,
          UserRole.Dvm,
          UserRole.Operations,
          UserRole.RegionalUser,
        ],
      },
      {
        key: Routes.DashboardsDonors,
        label: t('dashboardDonors'),
        permissions: [PermissionsEnum.ViewReports],
      },
      {
        key: Routes.DashboardsDonations,
        label: t('dashboardDonations'),
        permissions: [PermissionsEnum.ViewReports],
      },
      {
        key: Routes.DashboardsOwners,
        label: t('dashboardOwners'),
        permissions: [PermissionsEnum.ViewReports],
      },
    ],
    permissions: [PermissionsEnum.ViewReports],
  },
  {
    key: `${Routes.Reports}`,
    label: t('reports'),
    permissions: [PermissionsEnum.ViewReports],
  },
  {
    key: Routes.Settings,
    label: t('settings'),
    roles: [
      UserRole.Admin,
      UserRole.MedicalDirector,
      UserRole.Dvm,
      UserRole.CanineHandler,
      UserRole.TeamLead,
      UserRole.Operations,
      UserRole.RegionalUser,
      UserRole.Phlebotomist,
    ],
  },
];

const checkIfUserHasCanViewMenuItem = (
  userPermissions: PermissionsType,
  userRole: UserRole,
  menuItem: {
    key: Routes;
    label: string;
    permissions?: PermissionsEnum[];
    roles?: UserRole[];
  }
) => {
  const hasPermissions = menuItem.permissions
    ? menuItem.permissions.every((item) => userPermissions[item])
    : true;

  const hasRole = menuItem.roles ? menuItem.roles.includes(userRole) : true;

  return hasPermissions && hasRole;
};

const menuItemReducer = (
  previousItems: any,
  currentItem: any,
  userPermissions: PermissionsType,
  userRole: UserRole
) => {
  return checkIfUserHasCanViewMenuItem(userPermissions, userRole, currentItem)
    ? [
        ...previousItems,
        {
          ...currentItem,
          children: currentItem.children
            ? currentItem.children?.reduce(
                (previousChildrenItems: any, currentChildrenItem: any) =>
                  menuItemReducer(
                    previousChildrenItems,
                    currentChildrenItem,
                    userPermissions,
                    userRole
                  ),
                [] as any[]
              )
            : undefined,
        },
      ]
    : previousItems;
};

export const getMenuItemsOfInternalTeam = (
  role: RoleOutput,
  permissions: PermissionsType
): MenuOptionType[] => {
  return getMenuItemsOfInternalTeamPreset(role.value).reduce(
    (previousMenuItems, currentMenuItem) =>
      menuItemReducer(
        previousMenuItems,
        currentMenuItem,
        permissions,
        role.value
      ),
    [] as MenuOptionType[]
  );
};
